// canonicalAutofill — single source of truth for the read cascade
// that turns a chosen canonical into a fully-axed item draft.
//
// Today the same cascade is reimplemented across AddDraftSheet,
// ShopMode, ShopModeCheckout, ItemCard. That duplication is the
// reason "pasta gets categorized as pork" — one of the implementations
// has a different inference order than the others. Centralizing here
// means every inflow path makes identical decisions.
//
// Tier order (most specific → most generic):
//
//   1. UPC correction        if barcodeUpc supplied — strongest signal
//   2. (canonical, brand) correction  via manual_corrections (post-rebuild)
//   3. canonical-only correction      via manual_corrections w/ brand=null
//   4. Bundled canonical metadata     storage.location, category, subtype
//   5. DB ingredient_info             from useIngredientInfo dbMap
//   6. tagHintsToAxes                 if categoryHints supplied (OFF/USDA)
//   7. inferFoodTypeFromName          last-resort text match
//   8. Hard defaults                  defaultLocationForCategory
//
// This function is ASYNC because tiers 1-3 may need to call
// findBarcodeCorrection (network). Tiers 4-8 are pure / synchronous;
// callers with no UPC/brand can call autofillSync(canonicalId, ...)
// instead.

import { findIngredient, defaultStateFor, getCanonicalHome, getCanonicalTile } from "../data/ingredients";
import { parsePackageSize } from "./canonicalResolver";
import { typeIdForCanonical, inferFoodTypeFromName, FOOD_TYPES } from "../data/foodTypes";
import { tagHintsToAxes } from "./tagHintsToAxes";
import { defaultLocationForCategory } from "./usePantry";
import { findBarcodeCorrection } from "./barcodeCorrections";

// Pull the typeLabel out of the FOOD_TYPES catalog so the chip can
// render "Cheese" instead of "wweia_cheese".
function typeLabelFor(typeId) {
  if (!typeId) return null;
  const ft = FOOD_TYPES?.find?.((t) => t.id === typeId);
  return ft?.label || ft?.name || typeId;
}

// extractFromIngredientInfo — pulls placement axes from the canonical
// metadata cascade. Reads through `getCanonicalHome` so we get the
// declarative home (DB ingredient_info → bundled INGREDIENT_INFO →
// hub-chain inheritance) instead of re-implementing the walk here.
//
// arborio_rice, for example, has no INGREDIENT_INFO entry of its own
// but inherits `storage: { location: "pantry", tile: { pantry:
// "pasta_grains" } }` from rice_hub via the hub chain. Without the
// chain walk, this function would return location=null and the chip
// fell through to a category fallback that landed in fridge·misc.
function extractFromIngredientInfo(ing, info) {
  const out = {};
  if (info?.category) out.category = info.category;
  if (ing?.category) out.category = out.category || ing.category;
  if (ing?.subtype) out.subtype = ing.subtype;

  // Read the canonical's natural home through the cascade. This is
  // what makes "rice → pantry · pasta_grains" a property of the
  // DATA instead of a heuristic spread across multiple call sites.
  const home = ing ? getCanonicalHome(ing.id, info) : null;
  if (home?.location) out.location = home.location;
  if (home?.tile && out.location) {
    const tile = typeof home.tile === "string"
      ? (home.location === out.location ? home.tile : null)
      : home.tile[out.location] || null;
    if (tile) out.tileId = tile;
  }

  // Final category-derived fallback when no hub declared a home —
  // applies to standalone canonicals (no parentId) and orphan slugs.
  if (out.location == null && out.category) {
    const fromCat = locationFromCategory(out.category);
    if (fromCat) out.location = fromCat;
  }
  return out;
}

function locationFromCategory(category) {
  if (!category) return null;
  if (["dairy", "meat", "seafood", "produce"].includes(category)) return "fridge";
  if (category === "frozen") return "freezer";
  return "pantry";
}

// extractFromCorrection — the correction payload from
// findBarcodeCorrection has these fields populated when a prior
// scan / vote landed there.
function extractFromCorrection(correction) {
  if (!correction || typeof correction !== "object") return {};
  const out = {};
  if (correction.canonicalId) out.canonicalId = correction.canonicalId;
  if (correction.tileId) out.tileId = correction.tileId;
  if (correction.location) out.location = correction.location;
  if (correction.typeId) out.typeId = correction.typeId;
  if (correction.state) out.state = correction.state;
  if (correction.cut) out.cut = correction.cut;
  if (correction.brand) out.brand = correction.brand;
  if (correction.packageSizeAmount != null) {
    out.packageAmount = correction.packageSizeAmount;
  }
  if (correction.packageSizeUnit) {
    out.packageUnit = correction.packageSizeUnit;
  }
  return out;
}

// canonicalAutofill — async; consults UPC correction tier first
// (network) when barcodeUpc supplied. Returns a flat object of
// axis values + a `source` string describing which tier filled
// each one.
//
// Args:
//   canonicalId — required; the chosen canonical
//   opts.barcodeUpc — optional; triggers tier-1 lookup
//   opts.brand      — optional; informs tier-2 (canonical+brand) lookup
//   opts.categoryHints — optional; OFF tags from a scan
//   opts.dbInfo     — optional; pre-fetched ingredient_info merged object
//                     (caller passes useIngredientInfo's getInfo result
//                     to skip a redundant lookup)
//   opts.name       — optional; raw product name for tier-7 fallback
//
// Returns: { canonicalId, brand, location, category, tileId, typeId,
//            typeLabel, state, cut, packageAmount, packageUnit,
//            source: "upc-correction" | "canonical-brand-correction" |
//                    "canonical-info" | "tag-hints" | "name-infer" |
//                    "default" }
export async function canonicalAutofill(canonicalId, opts = {}) {
  if (!canonicalId) return { source: "no-canonical" };
  const { barcodeUpc = null, brand = null, categoryHints = null,
          dbInfo = null, name = null } = opts;

  const ing = findIngredient(canonicalId);
  const out = { canonicalId, source: "default" };

  // Tier 1: UPC correction (strongest signal — taught by prior scans)
  if (barcodeUpc) {
    try {
      const correction = await findBarcodeCorrection(barcodeUpc);
      const fromCorrection = extractFromCorrection(correction);
      if (Object.keys(fromCorrection).length > 0) {
        Object.assign(out, fromCorrection);
        out.source = "upc-correction";
      }
    } catch (_e) {
      // Network failure on correction lookup is non-fatal; fall
      // through to canonical defaults. Don't block the autofill.
    }
  }

  // Tiers 4-5: Canonical metadata + DB ingredient_info overlay.
  // These are the "natural home" of the canonical regardless of UPC.
  // We apply them BEFORE tag-hints because the canonical's own
  // metadata is more specific than OFF taxonomy guesses.
  const fromInfo = extractFromIngredientInfo(ing, dbInfo);
  for (const [k, v] of Object.entries(fromInfo)) {
    if (out[k] == null) out[k] = v;
  }
  if (out.source === "default" && ing) out.source = "canonical-info";

  // Tier 6: tagHintsToAxes when OFF categoryHints supplied (scan path)
  if (categoryHints && Array.isArray(categoryHints) && categoryHints.length > 0) {
    const axes = tagHintsToAxes(categoryHints);
    if (axes) {
      if (out.location == null && axes.category) {
        // tagHintsToAxes returns category but not location; map it
        out.location = defaultLocationForCategory(axes.category);
      }
      if (out.tileId == null && axes.tileId) out.tileId = axes.tileId;
      if (out.typeId == null && axes.typeId) out.typeId = axes.typeId;
      if (out.category == null && axes.category) out.category = axes.category;
      if (out.state == null && axes.state) out.state = axes.state;
      if (out.source === "default") out.source = "tag-hints";
    }
  }

  // Tier 7: text-inference fallback (only when name supplied AND
  // typeId still empty). inferFoodTypeFromName is the same heuristic
  // FoodTypes use for unknowns.
  if (out.typeId == null && name) {
    const inferred = inferFoodTypeFromName(name);
    if (inferred) {
      out.typeId = inferred;
      if (out.source === "default") out.source = "name-infer";
    }
  }

  // Tier 7.5: parse package size from productName when no UPC
  // correction filled it. OFF productNames frequently embed sizes —
  // "Ocean Halo Nori Sheets 40G", "Häagen-Dazs Vanilla 14 fl oz",
  // "Tyson Chicken Breast 1 LB". Without this, the user types the
  // size manually after every UPC scan even though the data was in
  // the OFF payload. parsePackageSize handles count-only ("12 ct"),
  // mass+count combos ("16 oz (8 ct)"), and multipacks ("12 × 50g").
  if ((out.packageAmount == null || out.packageUnit == null) && name) {
    const parsed = parsePackageSize(name);
    if (parsed && Number.isFinite(parsed.amount) && parsed.unit) {
      if (out.packageAmount == null) out.packageAmount = parsed.amount;
      if (out.packageUnit == null) out.packageUnit = parsed.unit;
      if (out.source === "default") out.source = "name-size-parse";
    }
  }

  // typeId from canonical itself (cleaner than text inference)
  if (out.typeId == null && ing) {
    const fromCanonical = typeIdForCanonical(ing);
    if (fromCanonical) out.typeId = fromCanonical;
  }
  if (out.typeId) out.typeLabel = typeLabelFor(out.typeId);

  // State default from canonical (e.g., chicken → whole, butter → block)
  if (out.state == null && ing) {
    const def = defaultStateFor(ing);
    if (def) out.state = def;
  }

  // Tier 8: hard defaults
  if (out.location == null && out.category) {
    out.location = defaultLocationForCategory(out.category);
  }
  if (out.location == null) out.location = "pantry"; // last-resort

  // Brand passes through if caller supplied it
  if (brand) out.brand = brand;

  return out;
}

// canonicalAutofillSync — synchronous variant for callers without UPC
// (manual / wizard / photo paths). Skips tier 1 (network) and works
// purely from in-memory canonical data + tag hints + name inference.
//
// Use when:
//   - Manual pipeline picks a canonical from search
//   - Wizard reaches the canonical step
//   - Photo pipeline gets a canonical back from Haiku
export function canonicalAutofillSync(canonicalId, opts = {}) {
  if (!canonicalId) return { source: "no-canonical" };
  const { brand = null, categoryHints = null, name = null } = opts;
  // Accept either pre-resolved `dbInfo` OR a bare `getInfo` getter
  // function. Older call sites passed `{ getInfo: useIngredientInfo().getInfo }`
  // expecting the destructure to call it for them — but the destructure
  // silently dropped it and synthetic canonicals lost their enrichment.
  // Rather than break those call sites, resolve here defensively.
  let dbInfo = opts.dbInfo || null;
  if (!dbInfo && typeof opts.getInfo === "function") {
    try { dbInfo = opts.getInfo(canonicalId) || null; }
    catch { dbInfo = null; }
  }

  const ing = findIngredient(canonicalId);
  const out = { canonicalId, source: "default" };

  const fromInfo = extractFromIngredientInfo(ing, dbInfo);
  for (const [k, v] of Object.entries(fromInfo)) {
    if (out[k] == null) out[k] = v;
  }
  if (ing) out.source = "canonical-info";

  if (categoryHints && Array.isArray(categoryHints) && categoryHints.length > 0) {
    const axes = tagHintsToAxes(categoryHints);
    if (axes) {
      if (out.location == null && axes.category) {
        out.location = defaultLocationForCategory(axes.category);
      }
      if (out.tileId == null && axes.tileId) out.tileId = axes.tileId;
      if (out.typeId == null && axes.typeId) out.typeId = axes.typeId;
      if (out.category == null && axes.category) out.category = axes.category;
      if (out.state == null && axes.state) out.state = axes.state;
    }
  }

  if (out.typeId == null && name) {
    const inferred = inferFoodTypeFromName(name);
    if (inferred) out.typeId = inferred;
  }
  if (out.typeId == null && ing) {
    const fromCanonical = typeIdForCanonical(ing);
    if (fromCanonical) out.typeId = fromCanonical;
  }
  if (out.typeId) out.typeLabel = typeLabelFor(out.typeId);

  if (out.state == null && ing) {
    const def = defaultStateFor(ing);
    if (def) out.state = def;
  }

  // Tier 7.5: parse package size from productName when no UPC
  // correction filled it. Same behavior as the async variant —
  // see comment there for rationale.
  if ((out.packageAmount == null || out.packageUnit == null) && name) {
    const parsed = parsePackageSize(name);
    if (parsed && Number.isFinite(parsed.amount) && parsed.unit) {
      if (out.packageAmount == null) out.packageAmount = parsed.amount;
      if (out.packageUnit == null) out.packageUnit = parsed.unit;
    }
  }

  if (out.location == null && out.category) {
    out.location = defaultLocationForCategory(out.category);
  }
  if (out.location == null) out.location = "pantry";

  if (brand) out.brand = brand;
  return out;
}
